/**
 * OS-owner entry guard (PRD-004 Batch 2, owner surface).
 *
 * A sibling of lib/portal-guard.ts for the SEPARATE `/os` surface. The OS owner
 * is a `subscribers` row in `status='pending'` (Route A, scoping pass §1). The
 * portal entry guard (portal-guard.ts) bounces `pending` by construction
 * (its SERVED_STATUSES is active|paused|canceled), so an OS owner cannot use
 * `/portal`. This guard serves a `pending` subscriber IF AND ONLY IF they own a
 * non-deleted `os_engagements` row.
 *
 * Ownership is proven by RLS, not by a status read. `fetchOwnOsEngagement()`
 * reads through the `os_engagements_owner_select` policy (migration 0026),
 * which scopes to `is_owner(owner_subscriber_id) and deleted_at is null`. A
 * non-null result therefore IS proof the caller owns a live engagement; no
 * separate `subscribers.status` check is needed (and the @studio/api-schemas
 * `Subscriber` type does not even model the `pending` value the OS owner row
 * carries, so reading it would mistype the surface).
 *
 * Outcomes:
 *   - No session            -> requireSubscriberOrOperator redirects to
 *                              /auth/sign-in (Phase C contract).
 *   - Operator session      -> allowed through with isOperator=true and a null
 *                              engagement (QA / dashboard preview). Operators
 *                              have no `subscribers` row and so no engagement of
 *                              their own; the operator drives lifecycle from the
 *                              /eric dashboard, not from here.
 *   - Owner, has engagement -> allowed through with the loaded engagement.
 *   - Authenticated, no     -> /auth/sign-out, the same conservative posture as
 *     engagement               portal-guard.ts: an authenticated user who owns
 *                              no OS engagement does not belong on `/os`. Signs
 *                              them out and sends them to the marketing site
 *                              (https://yesplease.studio) where the OS intake /
 *                              subscribe flow lives.
 *
 * The OS owner NEVER drives the lifecycle (scoping pass §4 / §9): this surface
 * is read-only status display. There is no owner write path through this guard.
 */

import { redirect } from '@tanstack/react-router';
import { getCurrentSession, getRoleFromSession } from './auth.js';
import { requireSubscriberOrOperator } from './route-guards.js';
import { fetchOwnOsEngagement, type OwnOsEngagement } from './os.js';

export interface OsOwnerEntryContext {
  isOperator: boolean;
  engagement: OwnOsEngagement | null;
}

/**
 * Run from `/os`'s `beforeLoad`. Throws `redirect(...)` to halt navigation when
 * the caller is not an OS owner; returns a small context object the layout and
 * index consume otherwise.
 */
export async function requireOsOwnerEntry(opts?: {
  redirectFrom?: string;
}): Promise<OsOwnerEntryContext> {
  // Phase C auth gate first (redirects unauthenticated users to sign-in).
  await requireSubscriberOrOperator(
    opts?.redirectFrom !== undefined ? { redirectFrom: opts.redirectFrom } : {},
  );

  const session = await getCurrentSession();
  // requireSubscriberOrOperator already guarantees a session; the null check
  // keeps the types honest.
  if (!session) {
    throw redirect({ to: '/auth/sign-in' });
  }

  // Operators may view the OS surface to QA a render. They have no subscriber
  // row and so no engagement of their own; short-circuit with an operator
  // context. The operator drives the OS lifecycle from the /eric dashboard.
  if (getRoleFromSession(session) === 'operator') {
    return { isOperator: true, engagement: null };
  }

  // Ownership is proven by RLS: the owner-select policy returns the engagement
  // only to its owner. A null result means the caller owns no live engagement.
  const engagement = await fetchOwnOsEngagement();
  if (!engagement) {
    // Authenticated but not an OS owner. Conservative fallback, same as
    // portal-guard.ts: sign out and send to the marketing site.
    throw redirect({ to: '/auth/sign-out' });
  }

  return { isOperator: false, engagement };
}
